import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getProjectReview, saveProjectReview, remindDepartment } from '../services/scheduleService';
import useUIStore from '../store/uiStore';
import { formatDateDDMMYY } from '../utils/formatDate';
import { formatSlip } from '../constants/schedule';

// PM weekly review for one project (layout approved 2026-10-25):
// 1. department updates this week + changes affecting handover
// 2. weekly summary that feeds Monday's management email.
// Validation mirrors save_review in schedule_review_routes.py.
const FIELDS = [
  { key: 'done_this_week', label: 'Done this week' },
  { key: 'plan_next_week', label: 'Plan next week' },
  { key: 'risks_issues', label: 'Risks and issues' },
  { key: 'needed_from_client', label: 'Needed from client' },
];
const EMPTY = { overall_status: '', status_reason: '', done_this_week: '', plan_next_week: '', risks_issues: '', needed_from_client: '' };

const WeeklyReviewPage = () => {
  const { projectId } = useParams();
  const showToast = useUIStore((s) => s.showToast);
  const [data, setData] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [reminding, setReminding] = useState(null);

  const load = () =>
    getProjectReview(projectId)
      .then((res) => {
        setData(res.data);
        const r = res.data.review;
        setForm(r ? Object.fromEntries(Object.keys(EMPTY).map((k) => [k, r[k] || ''])) : EMPTY);
      })
      .catch((err) => showToast(err.response?.data?.detail || 'Failed to load review', 'error'));

  useEffect(() => { load(); }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (key, value) => { setForm((f) => ({ ...f, [key]: value })); setError(''); };

  const save = async (submit) => {
    if (submit) {
      if (!form.overall_status) return setError('Choose an overall status before submitting');
      if (form.overall_status !== 'green' && !form.status_reason.trim()) return setError('Add a reason for amber or red status');
    }
    setSaving(true);
    try {
      const res = await saveProjectReview(projectId, { ...form, overall_status: form.overall_status || null, submit });
      showToast(res.data.message, 'success');
      load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const remind = async (dept) => {
    setReminding(dept.department_id);
    try {
      const res = await remindDepartment(projectId, dept.department_id);
      showToast(res.data.message, 'success');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to send reminder', 'error');
    } finally {
      setReminding(null);
    }
  };

  if (!data) return <div className="min-h-screen p-6"><div className="max-w-4xl mx-auto glass-card p-6 text-sm text-gray-500">Loading...</div></div>;

  const { review, editable } = data;
  const reviewState = review?.status === 'submitted' ? 'Submitted' : review ? 'Draft' : 'Not started';
  const label = 'block text-xs text-gray-500 mb-1';
  const area = 'w-full border border-gray-300 rounded px-2 py-1.5 text-sm min-h-[72px] disabled:bg-gray-50';

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-4xl mx-auto">
        <Link to="/schedule/reviews" className="text-sm text-white/80 hover:underline">&larr; Weekly Review</Link>
        <div className="flex items-baseline justify-between flex-wrap gap-2 mt-2 mb-4">
          <h1 className="text-xl font-bold text-white">{data.project_name} · weekly review</h1>
          <span className="text-sm text-white/80">
            Week {formatDateDDMMYY(data.week_start)} to {formatDateDDMMYY(data.week_end)} ·{' '}
            <span className={`text-xs px-2 py-0.5 rounded ${review?.status === 'submitted' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
              {reviewState}{review?.status !== 'submitted' && ' · due Sat 6 PM'}
            </span>
          </span>
        </div>

        <div className="glass-card p-5 mb-4">
          <h2 className="font-semibold text-gray-900 mb-3">1. Department updates this week</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {data.departments.map((d) => (
              <div key={d.department_id ?? 'none'} className={`rounded p-2 text-xs ${d.updated ? 'bg-gray-50' : 'bg-red-50 text-red-800'}`}>
                <div className="font-semibold">{d.updated ? '✓' : '⏱'} {d.department_name}</div>
                <div className={d.updated ? 'text-gray-500' : ''}>
                  {d.owners.join(', ') || 'No head set'} · {d.updated ? (d.slips ? `${d.slips} slip${d.slips > 1 ? 's' : ''}` : 'updated') : 'not updated'}
                </div>
                {!d.updated && editable && d.department_id && d.owners.length > 0 && (
                  <button className="mt-1 text-komal-blue hover:underline disabled:opacity-60" disabled={reminding === d.department_id}
                    onClick={() => remind(d)}>{reminding === d.department_id ? 'Sending...' : `Remind ${d.department_name}`}</button>
                )}
              </div>
            ))}
          </div>

          <h3 className="text-sm font-semibold text-gray-900 mt-4 mb-1">Changes affecting handover</h3>
          {data.handover_changes.length === 0 ? (
            <p className="text-sm text-gray-500">No new slips entered this week.</p>
          ) : data.handover_changes.map((c, i) => (
            <p key={i} className="text-sm mb-1">
              <span className={`text-xs px-2 py-0.5 rounded font-semibold mr-2 ${c.critical ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}`}>
                {c.critical ? 'Critical' : 'Float'}
              </span>
              {c.step_name} forecast → {formatDateDDMMYY(c.new_end)} ({c.department_name}{c.delay_cause ? `, cause: ${c.delay_cause.replace('_', ' ')}` : ''}).{' '}
              {c.critical ? `Handover moves ${formatSlip(c.handover_slip_days)}.` : 'Absorbed by float so far.'}
            </p>
          ))}
          <p className="text-xs text-gray-500 mt-2">
            Forecast handover {formatDateDDMMYY(data.schedule.forecast_handover)} ({formatSlip(data.schedule.handover_slip_days)} vs Rev {data.schedule.current_rev}) ·{' '}
            <Link to={`/projects/${projectId}`} className="text-komal-blue hover:underline">Open schedule</Link>
          </p>
          {data.can_plan && (
            <div className="mt-3">
              <Link to={`/projects/${projectId}/revision`}
                className="inline-block px-3 py-1.5 text-sm rounded border border-komal-blue text-komal-blue hover:bg-komal-blue/5 font-semibold">
                Raise revision
              </Link>
            </div>
          )}
        </div>

        <div className="glass-card p-5">
          <h2 className="font-semibold text-gray-900 mb-3">
            2. Weekly summary <span className="text-xs font-normal text-gray-500">(goes into Monday's management email)</span>
          </h2>
          <span className={label}>Overall status</span>
          <div className="flex gap-4 text-sm mb-3">
            {['green', 'amber', 'red'].map((s) => (
              <label key={s} className="flex items-center gap-1 capitalize">
                <input type="radio" name="overall" value={s} checked={form.overall_status === s} disabled={!editable}
                  onChange={() => set('overall_status', s)} /> {s}
              </label>
            ))}
          </div>
          <label className="block mb-3">
            <span className={label}>Reason for status (required for amber or red)</span>
            <textarea className={area} value={form.status_reason} disabled={!editable} onChange={(e) => set('status_reason', e.target.value)} />
          </label>
          <div className="grid md:grid-cols-2 gap-3">
            {FIELDS.map((f) => (
              <label key={f.key} className="block">
                <span className={label}>{f.label}</span>
                {f.key === 'done_this_week' && data.last_week_plan && (
                  <div className="text-xs text-gray-400 whitespace-pre-line mb-1 border-l-2 border-gray-200 pl-2">
                    Last week's plan: {data.last_week_plan}
                  </div>
                )}
                <textarea className={area} value={form[f.key]} disabled={!editable} onChange={(e) => set(f.key, e.target.value)} />
              </label>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-2">One item per line.</p>
          {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
          {editable ? (
            <div className="flex justify-end items-center gap-3 mt-4 flex-wrap">
              <span className="text-xs text-gray-500">Editable until the email goes out Monday 9 AM</span>
              <button onClick={() => save(false)} disabled={saving} className="px-4 py-2 text-sm rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-60">Save draft</button>
              <button onClick={() => save(true)} disabled={saving}
                className="px-4 py-2 text-sm rounded bg-komal-blue hover:bg-komal-blue-dark text-white font-semibold disabled:opacity-60">
                {review?.status === 'submitted' ? 'Update submitted review' : 'Submit review'}
              </button>
            </div>
          ) : (
            <p className="text-xs text-gray-500 mt-4">
              {data.can_plan ? "This week's review is closed - the Monday email has gone out." : "Only the project's PM can edit this review."}
            </p>
          )}
          {review?.submitted_by_name && (
            <p className="text-xs text-gray-400 mt-2">Submitted by {review.submitted_by_name} on {formatDateDDMMYY(review.submitted_at?.slice(0, 10))}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default WeeklyReviewPage;
